import { makeAutoObservable, runInAction } from "mobx";
import { customDisclosureService } from "../services/custom-disclosure.service";
import { showToast } from "@/lib/utils/toast";

class CustomDisclosureStore {
  // State
  disclosures = [];
  templates = [];
  analytics = null;
  loading = false;
  pagination = {
    total: 0,
    page: 1,
    limit: 100,
    totalPages: 0,
  };

  // Filters
  searchQuery = "";
  categoryFilter = "";
  statusFilter = "active"; // active, archived, all
  selectedIds = [];

  constructor() {
    makeAutoObservable(this);
  }

  // Computed
  get filteredDisclosures() {
    let filtered = [...this.disclosures];

    // Apply local filters (if needed)
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.code.toLowerCase().includes(query) ||
          d.title.toLowerCase().includes(query),
      );
    }

    return filtered;
  }

  get hasSelection() {
    return this.selectedIds.length > 0;
  }

  // Actions

  setSearchQuery = (query) => {
    this.searchQuery = query;
  };

  setCategoryFilter = (category) => {
    this.categoryFilter = category === "all" ? "" : category;
  };

  setStatusFilter = (status) => {
    this.statusFilter = status;
    this.loadDisclosures(); // Reload with new filter
  };

  toggleSelection = (id) => {
    if (this.selectedIds.includes(id)) {
      this.selectedIds = this.selectedIds.filter((sid) => sid !== id);
    } else {
      this.selectedIds.push(id);
    }
  };

  selectAll = () => {
    this.selectedIds = this.disclosures.map((d) => d._id);
  };

  clearSelection = () => {
    this.selectedIds = [];
  };

  // API Actions

  loadDisclosures = async (options = {}) => {
    this.loading = true;
    try {
      const params = {
        search: this.searchQuery,
        category: this.categoryFilter,
        isActive:
          this.statusFilter === "active"
            ? "true"
            : this.statusFilter === "archived"
              ? "false"
              : undefined,
        page: options.page || this.pagination.page,
        limit: options.limit || this.pagination.limit,
        sortBy: options.sortBy || "-createdAt",
      };

      const response = await customDisclosureService.list(params);

      runInAction(() => {
        this.disclosures = response.disclosures || [];
        this.pagination = response.pagination || this.pagination;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  };

  loadTemplates = async () => {
    try {
      const response = await customDisclosureService.getTemplates();
      runInAction(() => {
        this.templates = response.templates || [];
      });
    } catch (error) {
      console.error("Failed to load templates:", error);
    }
  };

  loadAnalytics = async () => {
    try {
      const response = await customDisclosureService.getAnalytics();
      runInAction(() => {
        this.analytics = response.analytics || null;
      });
    } catch (error) {
      console.error("Failed to load analytics:", error);
    }
  };

  createDisclosure = async (data) => {
    const response = await customDisclosureService.create(data);
    runInAction(() => {
      this.disclosures.unshift(response.disclosure);
      this.pagination.total += 1;
    });
    return response.disclosure;
  };

  updateDisclosure = async (id, data) => {
    const response = await customDisclosureService.update(id, data);
    runInAction(() => {
      const index = this.disclosures.findIndex((d) => d._id === id);
      if (index !== -1) {
        this.disclosures[index] = response.disclosure;
      }
    });
    return response.disclosure;
  };

  deleteDisclosure = async (id, force = false) => {
    await customDisclosureService.delete(id, force);
    runInAction(() => {
      this.disclosures = this.disclosures.filter((d) => d._id !== id);
      this.selectedIds = this.selectedIds.filter((sid) => sid !== id);
      this.pagination.total -= 1;
    });
  };

  archiveDisclosure = async (id) => {
    const response = await customDisclosureService.archive(id);
    runInAction(() => {
      const index = this.disclosures.findIndex((d) => d._id === id);
      if (index !== -1) {
        if (this.statusFilter === "active") {
          this.disclosures.splice(index, 1);
        } else {
          this.disclosures[index] = response.disclosure;
        }
      }
    });
  };

  restoreDisclosure = async (id) => {
    const response = await customDisclosureService.restore(id);
    runInAction(() => {
      const index = this.disclosures.findIndex((d) => d._id === id);
      if (index !== -1) {
        if (this.statusFilter === "archived") {
          this.disclosures.splice(index, 1);
        } else {
          this.disclosures[index] = response.disclosure;
        }
      }
    });
  };

  cloneDisclosure = async (id) => {
    const response = await customDisclosureService.clone(id);
    runInAction(() => {
      this.disclosures.unshift(response.disclosure);
      this.pagination.total += 1;
    });
    return response.disclosure;
  };

  setTemplate = async (id, isTemplate) => {
    const response = await customDisclosureService.setTemplate(id, isTemplate);
    runInAction(() => {
      const index = this.disclosures.findIndex((d) => d._id === id);
      if (index !== -1) {
        this.disclosures[index] = response.disclosure;
      }
    });
  };

  bulkArchive = async () => {
    if (this.selectedIds.length === 0) return;
    await customDisclosureService.bulkArchive(this.selectedIds);
    await this.loadDisclosures();
    this.clearSelection();
  };

  bulkDelete = async (force = false) => {
    if (this.selectedIds.length === 0) return;
    await customDisclosureService.bulkDelete(this.selectedIds, force);
    await this.loadDisclosures();
    this.clearSelection();
  };

  // Action Handlers (with toast & confirmations)

  handleClone = async (disclosureId) => {
    try {
      await this.cloneDisclosure(disclosureId);
      showToast.success("Custom disclosure cloned successfully");
      await this.loadAnalytics();
    } catch (error) {
      showToast.error(error?.message || "Failed to clone disclosure");
    }
  };

  handleArchive = async (disclosureId) => {
    try {
      await this.archiveDisclosure(disclosureId);
      showToast.success("Custom disclosure archived");
      await this.loadAnalytics();
    } catch (error) {
      showToast.error(error?.message || "Failed to archive disclosure");
    }
  };

  handleRestore = async (disclosureId) => {
    try {
      await this.restoreDisclosure(disclosureId);
      showToast.success("Custom disclosure restored");
      await this.loadAnalytics();
    } catch (error) {
      showToast.error(error?.message || "Failed to restore disclosure");
    }
  };

  handleToggleTemplate = async (disclosureId, currentStatus) => {
    try {
      await this.setTemplate(disclosureId, !currentStatus);
      showToast.success(
        currentStatus ? "Removed from templates" : "Marked as template",
      );
      await this.loadAnalytics();
    } catch (error) {
      showToast.error(error?.message || "Failed to update template status");
    }
  };

  handleDelete = async (disclosureId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this custom disclosure?",
    );
    if (!confirmed) return;

    try {
      await this.deleteDisclosure(disclosureId, false);
      showToast.success("Custom disclosure deleted");
      await this.loadAnalytics();
    } catch (error) {
      if (error?.message?.includes("used in")) {
        const force = window.confirm(
          "This disclosure is used in reports. Delete anyway? This will remove all data.",
        );
        if (force) {
          try {
            await this.deleteDisclosure(disclosureId, true);
            showToast.success("Custom disclosure deleted");
            await this.loadAnalytics();
          } catch (err) {
            showToast.error(err?.message || "Failed to delete disclosure");
          }
        }
      } else {
        showToast.error(error?.message || "Failed to delete disclosure");
      }
    }
  };

  handleBulkArchive = async () => {
    try {
      await this.bulkArchive();
      showToast.success("Custom disclosures archived");
      await this.loadAnalytics();
    } catch (error) {
      showToast.error(error?.message || "Failed to archive disclosures");
    }
  };

  handleBulkDelete = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete selected disclosures?",
    );
    if (!confirmed) return;

    try {
      await this.bulkDelete(false);
      showToast.success("Custom disclosures deleted");
      await this.loadAnalytics();
    } catch (error) {
      if (error?.message?.includes("in use")) {
        const force = window.confirm(
          "Some disclosures are in use. Delete anyway?",
        );
        if (force) {
          await this.bulkDelete(true);
          showToast.success("Custom disclosures deleted");
          await this.loadAnalytics();
        }
      } else {
        showToast.error(error?.message || "Failed to delete disclosures");
      }
    }
  };

  reset = () => {
    this.disclosures = [];
    this.templates = [];
    this.analytics = null;
    this.loading = false;
    this.pagination = {
      total: 0,
      page: 1,
      limit: 100,
      totalPages: 0,
    };
    this.searchQuery = "";
    this.categoryFilter = "";
    this.statusFilter = "active";
    this.selectedIds = [];
  };
}

export const customDisclosureStore = new CustomDisclosureStore();
